import React, { useState, useEffect } from "react";
import { MdOutlineDateRange } from "react-icons/md";
import { FaRegComments } from "react-icons/fa6";
import { TfiPencil } from "react-icons/tfi";
import API from "../../../Utils/api";
import { useNavigate } from "react-router-dom";

// Skeleton Card
const BlogSkeleton = () => (
  <div className="flex flex-col bg-white p-5 items-start w-72 border animate-pulse flex-shrink-0">
    <div className="w-full h-48 bg-gray-200" />
    <div className="flex gap-5 mt-3 w-full">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-3 bg-gray-200 rounded w-1/3" />
    </div>
    <div className="h-4 bg-gray-200 rounded w-full mt-4" />
    <div className="h-4 bg-gray-200 rounded w-3/4 mt-2" />
  </div>
);

const Blog = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchBlogs = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/blogs/all-blogs");
      const data = Array.isArray(res.data) ? res.data : res.data.data || [];
      setBlogs(data);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError("Failed to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Strip HTML tags for preview
  const stripHtml = (html) => html?.replace(/<[^>]*>/g, "") || "";

  // Loop blogs for scroll animation (need enough items)
  const loopedData = blogs.length > 0 ? [...blogs, ...blogs, ...blogs] : [];

  return (
    <div className="w-full px-4 sm:px-6 md:px-10 py-4 sm:py-8 bg-yellow-50/30 mb-5">
      {/* Header */}
      <div className="flex flex-col items-center justify-center text-center">
        <p className="text-[#92AD36] text-sm font-semibold tracking-wide">
          OUR COMPANY BLOG
        </p>
        <h2 className="text-2xl md:text-4xl font-serif  text-[#000000] mt-3">
          Check Our Latest Blog
        </h2>
        <div className="flex mt-4 gap-2">
          <span className="w-10 h-1 bg-[#92AD36]"></span>
          <span className="w-3 h-1 bg-[#92AD36]"></span>
          <span className="w-5 h-1 bg-[#92AD36]"></span>
        </div>
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="flex flex-col items-center py-12 text-center">
          <span className="text-4xl mb-3">😕</span>
          <p className="text-gray-500 text-sm">{error}</p>
          <button
            onClick={fetchBlogs}
            className="mt-3 px-5 py-1.5 bg-[#92AD36] text-white rounded-lg text-xs hover:bg-[#7a9230] transition"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && blogs.length === 0 && (
        <div className="flex flex-col items-center py-12 text-center">
          <span className="text-4xl mb-3">📝</span>
          <p className="text-gray-500 text-sm">No blogs published yet</p>
        </div>
      )}

      {/* Scrolling Blog Cards */}
      {(loading || blogs.length > 0) && (
        <div className="overflow-hidden mt-8">
          <div className="flex animate-scroll gap-5 w-max">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <BlogSkeleton key={i} />
                ))
              : loopedData.map((item, index) => (
                  <div
                    onClick={() => navigate("/blog")}
                    key={`${item._id}-${index}`}
                    className="flex flex-col bg-white p-5 items-start w-72 border hover:border-[#92AD36] hover:shadow-xl hover:backdrop-blur-sm hover:scale-90 duration-500 flex-shrink-0"
                  >
                    {/* Image */}
                    <div className="w-full relative">
                      <img
                        src={item.images?.[0]}
                        alt={item.title}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/400x200?text=Blog";
                        }}
                      />
                      {/* Corner pencil badge */}
                      <div className="absolute top-0 right-0 w-0 h-0 border-t-[60px] border-t-[#92AD36] border-l-[60px] border-l-transparent">
                        <TfiPencil
                          size={17}
                          className="text-white absolute -top-12 right-2"
                        />
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="flex gap-5 mt-3 text-xs text-[#9DABAF]">
                      <span className="flex items-center gap-1.5">
                        <MdOutlineDateRange size={13} />
                        {formatDate(item.publishedDate || item.createdAt)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <FaRegComments size={13} />0 comments
                      </span>
                    </div>

                    {/* Title */}
                    <p className="mt-4 text-base text-black font-semibold line-clamp-2 leading-snug">
                      {item.title}
                    </p>

                    {/* Content preview */}
                    <p className="mt-2 text-xs text-[#1E1E1E] line-clamp-2 leading-relaxed">
                      {stripHtml(item.content)}
                    </p>
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;
